import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/39welo3a_Barefoot1879.jpg";

// Fade-in animation props, gated on page load
const fadeIn = (isLoaded, duration, delay = 0) => ({
  initial: { opacity: 0 },
  animate: isLoaded ? { opacity: 1 } : {},
  transition: { duration, delay, ease: "easeOut" }
});

const Hero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  // Trigger animation after page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500); // Wait 500ms after page load
    return () => clearTimeout(timer);
  }, []);

  const scrollToNext = () => {
    const welcomeSection = document.querySelector('#welcome');
    if (welcomeSection) {
      welcomeSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Respiro Hotel"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        {/* Logo Text - appears after page load */}
        <motion.h1
          {...fadeIn(isLoaded, 4)}
          style={{ 
            fontFamily: '"IvyPresto Text", serif', 
            fontWeight: 300,
            WebkitFontSmoothing: 'subpixel-antialiased',
            letterSpacing: '0.4em'
          }}
          className="text-5xl md:text-7xl lg:text-8xl text-white mb-6 ml-[0.4em]"
          data-testid="hero-title"
        >
          {t('hero.title')}
        </motion.h1>

        {/* Subtitle - appears after title */}
        <motion.p
          {...fadeIn(isLoaded, 3.5, 2)}
          className="font-sans uppercase tracking-[0.4em] text-xs md:text-sm text-white ml-[0.4em]"
          data-testid="hero-subtitle"
        >
          {t('hero.subtitle')}
        </motion.p>
      </div>

      {/* Opening Date - positioned absolutely between subtitle and scroll indicator */}
      <motion.p
        {...fadeIn(isLoaded, 3, 3)}
        className="absolute left-1/2 -translate-x-1/2 top-[68%] z-10 font-sans uppercase tracking-[0.3em] text-sm md:text-lg lg:text-xl text-white text-center whitespace-nowrap"
        data-testid="hero-opening"
      >
        OPENING SPRING 2027
      </motion.p>

      {/* Scroll Indicator */}
      <motion.div
        {...fadeIn(isLoaded, 2, 4.5)}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center"
      >
        <button
          onClick={scrollToNext}
          className="flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors duration-300 cursor-pointer"
          data-testid="hero-scroll-btn"
          aria-label={t('hero.scroll')}
        >
          <span className="uppercase tracking-[0.2em] text-[10px]">{t('hero.scroll')}</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown size={20} />
          </motion.div>
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
